import { useEffect, useId, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useSWRMutation from 'swr/mutation'
import { z } from 'zod'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  cn,
} from '@ludus/ui'
import type { ReactNode } from 'react'
import {
  managerDisplayName,
  asManagerId,
  asStatusId,
  type PipelineId,
  type StatusId,
} from '@ludus/core'
import { createLead, PipelineApiError, type CreateLeadInput } from '@/lib/api/pipelines.ts'
import { apiErrorMessage } from '@/shared/feedback/apiErrorMessage.ts'
import { usePipelineManagers, usePipelineStages } from '../../data/usePipelinesSource.ts'

interface NewLeadDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  /** The pipeline this dialog creates leads into. Threaded down by `PipelineHeader`. */
  pipelineId: PipelineId
  /** Stage to default the form to. Falls back to the first pipeline stage when null. */
  defaultStageId?: StatusId | null
}

/**
 * Form schema. Minimal quick-create surface — only the three fields a lead
 * cannot exist without (name, stage, owner). Larger edits land on the lead
 * detail route after redirect. Manager is required here even though the DB
 * column is nullable: with no role system, an un-owned lead has no fallback
 * owner.
 */
const newLeadSchema = z.object({
  name: z.string().trim().min(1, 'required'),
  stageId: z.string().min(1, 'required'),
  managerId: z.string().min(1, 'required'),
})

type NewLeadFormValues = z.infer<typeof newLeadSchema>

export function NewLeadDialog({
  open,
  onOpenChange,
  pipelineId,
  defaultStageId,
}: NewLeadDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const stages = usePipelineStages()
  const managers = usePipelineManagers()

  const formId = useId()

  const stageOptions = useMemo(
    () => stages.map((s) => ({ value: s.id as string, label: s.name })),
    [stages],
  )
  const managerOptions = useMemo(
    () => managers.map((m) => ({ value: m.id as string, label: managerDisplayName(m) })),
    [managers],
  )

  const initialStageId = (defaultStageId ?? stages[0]?.id ?? '') as string
  const initialManagerId = (managers[0]?.id ?? '') as string
  const defaultName = t('pages.pipelines.newLead.nameDefault', { defaultValue: 'New deal' })

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewLeadFormValues>({
    resolver: zodResolver(newLeadSchema),
    defaultValues: {
      name: defaultName,
      stageId: initialStageId,
      managerId: initialManagerId,
    },
  })

  const [submitError, setSubmitError] = useState<PipelineApiError | null>(null)

  // Unique-per-pipeline mutation key — the read cache uses `/pipelines/:id/leads`
  // for the GET listing, so we suffix to avoid colliding with it.
  const { trigger, isMutating } = useSWRMutation(
    `/pipelines/${pipelineId}/leads:create`,
    (_key: string, { arg }: { arg: CreateLeadInput }) => createLead(pipelineId, arg),
  )

  // Reset the form whenever the dialog re-opens — keeps prior submissions /
  // dismissed values from leaking into a fresh creation flow.
  useEffect(() => {
    if (open) {
      reset({
        name: defaultName,
        stageId: initialStageId,
        managerId: initialManagerId,
      })
      setSubmitError(null)
    }
  }, [open, defaultName, initialStageId, initialManagerId, reset])

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)
    try {
      const lead = await trigger({
        name: values.name,
        statusId: asStatusId(values.stageId),
        assignedManagerId: asManagerId(values.managerId),
      })
      onOpenChange(false)
      navigate(`/pipelines/${pipelineId}/leads/${lead.id}`)
    } catch (err) {
      // useSWRMutation reject path. classifyError in lib/api/pipelines wraps
      // every failure as PipelineApiError; surface .kind + .status for the UI.
      setSubmitError(
        err instanceof PipelineApiError
          ? err
          : new PipelineApiError({ kind: 'unknown', status: null, message: 'submit failed' }),
      )
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <div className="min-w-0">
            <DialogTitle>
              {t('pages.pipelines.newLead.title', { defaultValue: 'New deal' })}
            </DialogTitle>
            <DialogDescription>
              {t('pages.pipelines.newLead.description', {
                defaultValue:
                  'Create a new lead in this pipeline. You can fill in the rest on the lead page.',
              })}
            </DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>
        <DialogBody className="py-5">
          <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-5">
            <Field
              label={t('pages.pipelines.newLead.name', { defaultValue: 'Name' })}
              error={errors.name?.message}
            >
              <Input
                {...register('name')}
                autoFocus
                placeholder={t('pages.pipelines.newLead.namePlaceholder', {
                  defaultValue: 'e.g. Brow course — Anna Petrenko',
                })}
                aria-invalid={!!errors.name}
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field
                label={t('pages.pipelines.newLead.stage', { defaultValue: 'Stage' })}
                error={errors.stageId?.message}
              >
                <Controller
                  name="stageId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      options={stageOptions}
                      onValueChange={(v) => field.onChange(v)}
                      className="w-full"
                      triggerClassName="w-full"
                    />
                  )}
                />
              </Field>
              <Field
                label={t('pages.pipelines.newLead.owner', { defaultValue: 'Owner' })}
                error={errors.managerId?.message}
              >
                <Controller
                  name="managerId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      options={managerOptions}
                      onValueChange={(v) => field.onChange(v)}
                      className="w-full"
                      triggerClassName="w-full"
                    />
                  )}
                />
              </Field>
            </div>
            {submitError && (
              <p role="alert" className="text-[11px] font-medium text-toast-error-text">
                {apiErrorMessage(submitError, t, 'pages.pipelines.newLead')}
              </p>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isMutating}
          >
            {t('pages.pipelines.newLead.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="submit" form={formId} variant="primary" size="sm" disabled={isMutating}>
            {t('pages.pipelines.newLead.create', { defaultValue: 'Create' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface FieldProps {
  label: string
  error?: string
  children: ReactNode
  className?: string
}

function Field({ label, error, children, className }: FieldProps) {
  return (
    <label className={cn('flex flex-col gap-1.5', className)}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
        {label}
      </span>
      {children}
      {error && (
        <span role="alert" className="text-[11px] font-medium text-toast-error-text">
          {error}
        </span>
      )}
    </label>
  )
}
